/**
 * ProspectPortalPage Component
 * Main portal page - fetches prospect by token, renders sections based on config
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useProspect } from '../../hooks/useProspect';
import { useTracking } from '../../hooks/useTracking';
import { getVisibleSections } from '../../utils/sections';
import SectionWrapper from '../shared/SectionWrapper';

// Section imports
import HeroSection from './sections/HeroSection';
import ProgressBar from './sections/ProgressBar';
import WhyTalkAustralia from './sections/WhyTalkAustralia';
import PainPointsSection from './sections/PainPointsSection';
import ProposalCard from './sections/ProposalCard';
import DepartmentMap from './sections/DepartmentMap';
import JourneyRoadmap from './sections/JourneyRoadmap';
import PortalFeaturesSection from './sections/PortalFeaturesSection';
import UseCasesSection from './sections/UseCasesSection';
import AlsoOfferSection from './sections/AlsoOfferSection';
import AppDownloadSection from './sections/AppDownloadSection';
import ChatWidget from './sections/ChatWidget';
import AcceptCTA from './sections/AcceptCTA';
import Footer from './sections/Footer';

const SECTION_COMPONENTS = {
  hero: HeroSection,
  'progress-bar': ProgressBar,
  'why-talk-australia': WhyTalkAustralia,
  'pain-points': PainPointsSection,
  'proposal-pricing': ProposalCard,
  'department-map': DepartmentMap,
  'journey-roadmap': JourneyRoadmap,
  'portal-features': PortalFeaturesSection,
  'use-cases': UseCasesSection,
  'also-offer': AlsoOfferSection,
  'app-download': AppDownloadSection,
  chat: ChatWidget,
  'accept-cta': AcceptCTA,
  footer: Footer,
};

const styles = {
  page: {
    minHeight: '100vh',
    backgroundColor: '#0F1729',
    color: '#FFFFFF',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#0F1729',
  },
  loadingSpinner: {
    border: '4px solid #2A3444',
    borderTop: '4px solid #0057FF',
    borderRadius: '50%',
    width: '40px',
    height: '40px',
    animation: 'spin 1s linear infinite',
  },
  errorContainer: {
    maxWidth: '600px',
    margin: '60px auto',
    padding: '40px',
    backgroundColor: '#1A2332',
    borderRadius: '8px',
    border: '1px solid #2A3444',
  },
  errorTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#FF6B6B',
    marginBottom: '12px',
  },
  errorMessage: {
    fontSize: '16px',
    color: '#BBBEC7',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
  errorAction: {
    display: 'inline-block',
    padding: '12px 24px',
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    textDecoration: 'none',
    borderRadius: '4px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    border: 'none',
  },
  acceptedContainer: {
    maxWidth: '600px',
    margin: '60px auto',
    padding: '60px 40px',
    backgroundColor: '#1A2332',
    borderRadius: '8px',
    border: '2px solid #00D4AA',
    textAlign: 'center',
  },
  acceptedIcon: {
    fontSize: '64px',
    marginBottom: '24px',
  },
  acceptedTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#00D4AA',
    marginBottom: '12px',
  },
  acceptedMessage: {
    fontSize: '16px',
    color: '#BBBEC7',
    marginBottom: '24px',
    lineHeight: '1.5',
  },
};

/**
 * ProspectPortalPage
 * Main portal component that orchestrates fetching and rendering
 */
function ProspectPortalPage({ token }) {
  const { prospect, status, error, isLoading, isExpired, isAccepted, sessionId } = useProspect(token);
  const { recordSectionView } = useTracking(prospect?.id, sessionId);
  const [acceptanceCompleted, setAcceptanceCompleted] = useState(false);

  // Add keyframe animation to document
  useEffect(() => {
    if (!document.querySelector('style[data-portal-animations]')) {
      const style = document.createElement('style');
      style.setAttribute('data-portal-animations', 'true');
      style.textContent = `
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `;
      document.head.appendChild(style);
    }
  }, []);

  // Get visible sections in order
  const visibleSections = useMemo(() => {
    if (!prospect?.sectionConfig) return [];
    return getVisibleSections(prospect.sectionConfig);
  }, [prospect?.sectionConfig]);

  // Render loading state
  if (isLoading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner} />
      </div>
    );
  }

  // Render expired token state
  if (isExpired) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <div style={styles.errorTitle}>Portal Link Expired</div>
          <div style={styles.errorMessage}>
            This portal link is no longer valid. Please contact your account manager for a new link.
          </div>
          <button
            style={styles.errorAction}
            onClick={() => {
              window.location.href = 'https://talkaustralia.com.au/contact/';
            }}
          >
            Contact Us
          </button>
        </div>
      </div>
    );
  }

  // Render error state
  if (error && !prospect) {
    return (
      <div style={styles.page}>
        <div style={styles.errorContainer}>
          <div style={styles.errorTitle}>Oops!</div>
          <div style={styles.errorMessage}>{error.message || 'Unable to load portal'}</div>
          <button
            style={styles.errorAction}
            onClick={() => window.location.reload()}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Render accepted state (after acceptance completed)
  if (isAccepted || acceptanceCompleted) {
    return (
      <div style={styles.page}>
        <div style={styles.acceptedContainer}>
          <div style={styles.acceptedIcon}>✓</div>
          <div style={styles.acceptedTitle}>Proposal Accepted!</div>
          <div style={styles.acceptedMessage}>
            Thank you for accepting the proposal. Our team will be in touch soon to discuss next steps.
          </div>
          <button
            style={styles.errorAction}
            onClick={() => {
              window.location.href = 'https://talkaustralia.com.au/';
            }}
          >
            Back to Talk Australia
          </button>
        </div>
      </div>
    );
  }

  // Render portal with sections
  return (
    <div style={styles.page}>
      {visibleSections.map((section) => {
        const SectionComponent = SECTION_COMPONENTS[section.id];
        if (!SectionComponent) {
          console.warn(`Unknown section: ${section.id}`);
          return null;
        }

        return (
          <SectionWrapper
            key={section.id}
            sectionId={section.id}
            visible={true}
            onSectionView={recordSectionView}
            animateIn={true}
          >
            <SectionComponent
              prospect={prospect}
              onAcceptanceCompleted={() => setAcceptanceCompleted(true)}
            />
          </SectionWrapper>
        );
      })}
    </div>
  );
}

ProspectPortalPage.propTypes = {
  token: function (props, propName) {
    if (typeof props[propName] !== 'string') {
      return new Error(`${propName} must be a string`);
    }
  },
};

export default ProspectPortalPage;
